import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { useAuth } from '../../context/AuthContext';
import ActionDrawer from '../components/shared/ActionDrawer';
import { showSuccess, showError, showServerError } from '../../utils/toastHelper';

function UserForm({ isOpen, onClose, user, onSuccess }) {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  // ... existing state ...
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    contactInfo: {
      phone: '',
      address: ''
    },
    role: '',
    status: 'ACTIVE',
    password: '',
    password_confirmation: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        contactInfo: {
          phone: user.contactInfo?.phone || '',
          address: typeof user.contactInfo?.address === 'object' ? user.contactInfo.address : {
            city: '',
            district: '',
            openAddress: user.contactInfo?.address?.openAddress || user.contactInfo?.address || ''
          }
        },
        role: user.role || '',
        status: user.status || 'ACTIVE',
        password: '',
        password_confirmation: ''
      });
    } else {
      let defaultRole = '';
      if (currentUser?.role === 'COMPANY_ADMIN') {
        defaultRole = 'SHIPPING_STAFF';
      }

      setFormData({
        fullName: '',
        email: '',
        contactInfo: {
          phone: '',
          address: {
            city: '',
            district: '',
            openAddress: ''
          }
        },
        role: defaultRole,
        status: 'ACTIVE',
        password: '',
        password_confirmation: ''
      });
    }
  }, [user, currentUser, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('contactInfo.address.')) {
      const field = name.split('.')[2];
      setFormData(prev => ({
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          address: {
            ...prev.contactInfo.address,
            [field]: value
          }
        }
      }));
    } else if (name.startsWith('contactInfo.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    if (!formData.fullName || !formData.email || !formData.role) {
      showError('Lütfen gerekli alanları doldurun');
      setLoading(false);
      return;
    }

    try {
      const endpoint = user ? `/admin/users/${user.id}` : '/admin/users';
      const method = user ? 'PUT' : 'POST';

      const dataToSend = {
        ...formData,
        contactInfo: {
          phone: formData.contactInfo.phone,
          address: formData.contactInfo.address
        }
      };

      if (user) {
        delete dataToSend.password;
        delete dataToSend.password_confirmation;
      }

      const response = await apiRequest(endpoint, {
        method,
        body: JSON.stringify(dataToSend)
      });

      if (response.success) {
        showSuccess(`Personel başarıyla ${user ? 'güncellendi' : 'oluşturuldu'}`);
        onSuccess();
      } else {
        showError(response.message || `Personel ${user ? 'güncellenirken' : 'oluşturulurken'} bir hata oluştu`);
      }
    } catch (error) {
      console.error('Failed to save staff:', error);
      if (error.isAuthError) return;
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || `Personel ${user ? 'güncellenirken' : 'oluşturulurken'} bir hata oluştu`);
      }
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-light border" onClick={onClose}>
        İptal
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Kaydediliyor...
          </>
        ) : (
          'Kaydet'
        )}
      </button>
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
      width="800px"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="user-form-content">
        <div className="row g-4">
          {/* Sol Kolon - Temel Bilgiler */}
          <div className="col-md-8">
            <div className="card shadow-sm mb-4 border-0">
              <div className="card-header bg-light bg-opacity-50 border-0">
                <h6 className="mb-0 fw-bold text-dark">Temel Bilgiler</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-muted small fw-medium">Ad Soyad</label>
                  <input
                    type="text"
                    className="form-control"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-muted small fw-medium">E-posta</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-muted small fw-medium">İletişim Bilgisi</label>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-muted small">Telefon</label>
                      <input
                        type="text"
                        className="form-control"
                        name="contactInfo.phone"
                        value={formData.contactInfo.phone}
                        onChange={handleChange}
                        placeholder="+90 555 123 4567"
                      />
                    </div>
                    <div className="col-12">
                      <h6 className="text-muted small fw-bold mt-2 mb-3">Adres Detayları</h6>
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label className="form-label text-muted small">İl</label>
                          <input
                            type="text"
                            className="form-control mb-2"
                            name="contactInfo.address.city"
                            value={formData.contactInfo.address?.city || ''}
                            onChange={handleChange}
                            placeholder="İl"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-muted small">İlçe</label>
                          <input
                            type="text"
                            className="form-control mb-2"
                            name="contactInfo.address.district"
                            value={formData.contactInfo.address?.district || ''}
                            onChange={handleChange}
                            placeholder="İlçe"
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label text-muted small">Sokak / Açık Adres</label>
                          <textarea
                            className="form-control"
                            name="contactInfo.address.openAddress"
                            value={formData.contactInfo.address?.openAddress || ''}
                            onChange={handleChange}
                            placeholder="Sokak, kapı no ve diğer detaylar..."
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!user ? (
              <div className="card shadow-sm border-0">
                <div className="card-header bg-light bg-opacity-50 border-0">
                  <h6 className="mb-0 fw-bold text-dark">Hesap Aktivasyonu</h6>
                </div>
                <div className="card-body">
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Kullanıcı oluşturulduktan sonra email adresine <strong>aktivasyon linki</strong> gönderilecek.
                    Kullanıcı bu link ile kendi şifresini belirleyecek ve hesabını aktive edecek.
                  </div>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm border-0">
                <div className="card-header bg-light bg-opacity-50 border-0 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold text-dark">Şifre İşlemleri</h6>
                </div>
                <div className="card-body">
                  <button
                    type="button"
                    className="btn btn-outline-warning w-100 mb-3"
                    onClick={async () => {
                      if (window.confirm('Kullanıcıya şifre sıfırlama bağlantısı göndermek istediğinize emin misiniz?')) {
                        try {
                          setLoading(true);
                          const response = await apiRequest(`/admin/users/${user.id}/reset-password`, {
                            method: 'POST'
                          });

                          if (response.success) {
                            showSuccess('Şifre sıfırlama bağlantısı gönderildi');
                          } else {
                            showError(response.message || 'Şifre sıfırlama bağlantısı gönderilemedi');
                          }
                        } catch (error) {
                          console.error('Failed to send reset password link:', error);
                          if (error.apiError) {
                            showServerError();
                          } else {
                            showError(error.message || 'Şifre sıfırlama bağlantısı gönderilemedi');
                          }
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                  >
                    <i className="fas fa-key me-2"></i>
                    Şifre Sıfırlama Bağlantısı Gönder
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Durum ve Rol */}
          <div className="col-md-4">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-light bg-opacity-50 border-0">
                <h6 className="mb-0 fw-bold text-dark">Durum ve Rol</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-muted small fw-medium">Rol</label>
                  <select
                    className="form-select"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    {currentUser?.role === 'SUPER_ADMIN' && (
                      <>
                        <option value="" disabled>Rol Seçiniz</option>
                        <option value="SUPER_ADMIN">Süper Admin</option>
                        <option value="COMPANY_ADMIN">Şirket Yöneticisi</option>
                        <option value="SHIPPING_STAFF">Kargo Personeli</option>
                        <option value="CUSTOMER">Müşteri</option>
                      </>
                    )}
                    {currentUser?.role === 'COMPANY_ADMIN' && (
                      <>
                        <option value="" disabled>Rol Seçiniz</option>
                        <option value="SHIPPING_STAFF">Kargo Personeli</option>
                        <option value="CUSTOMER">Müşteri</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label text-muted small fw-medium">Durum</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="ACTIVE">Aktif</option>
                    <option value="INACTIVE">Pasif</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </ActionDrawer>
  );
}

export default UserForm;
